import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { useOrgStructureSyncRun } from "./useOrgStructureSyncRun";
import { StageAlert } from "./StageAlert";

const REASON_LABEL = {
  never_synced: "Not synced with the Diagnostic yet",
  new_extraction: "New Functional extraction in the Diagnostic",
  rules_changed: "The sync rules changed",
  decisions_changed: "Decisions changed in the Diagnostic",
} as const;

const DRAFT_HINT_ID = "org-structure-sync-draft-hint";

/** Sincroniza só a estrutura organizacional com o Diagnóstico (spec D11). */
export function OrgStructureSyncAction({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { status: { data, isError, refetch }, hasDraft, pending, run } = useOrgStructureSyncRun(projectId);

  if (isError) {
    return <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-white/75">
      <span>{t("Could not load synchronization status.")}</span>
      <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>{t("Retry")}</Button>
    </div>;
  }
  if (!data) return null;
  if (data.needs_full_extraction) {
    return <p className="max-w-sm text-sm text-white/75">
      {t("Run the full Configuration Server extraction to bring the structure from the Diagnostic.")}
    </p>;
  }
  if (data.source_missing) {
    return <p className="max-w-sm text-sm text-white/75">
      {t("Run the Functional agent extraction to bring the structure from the Diagnostic.")}
    </p>;
  }
  if (!data.needs_sync) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {data.reason && <span className="text-sm text-white/75">{t(REASON_LABEL[data.reason])}</span>}
      <Button type="button" variant="action" className="h-10 gap-2 px-4 text-sm font-semibold"
        onClick={() => void run()} disabled={pending || hasDraft} aria-busy={pending}
        aria-describedby={hasDraft ? DRAFT_HINT_ID : undefined}>
        <RefreshCw aria-hidden="true" className="h-4 w-4" />
        {t("Sync with the Diagnostic")}
      </Button>
      {hasDraft && <span id={DRAFT_HINT_ID} className="text-sm text-white/75">{t("Save or discard your changes before syncing.")}</span>}
    </div>
  );
}

/**
 * O alerta amarelo da lista da estrutura (Gerenciamento e Execução): o Diagnóstico mudou
 * e o botão sincroniza ali mesmo. Só aparece quando há o que sincronizar.
 */
export function OrgStructureSyncAlert({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { needsSync, hasDraft, pending, run } = useOrgStructureSyncRun(projectId);
  if (!needsSync) return null;
  return (
    <StageAlert
      message={hasDraft
        ? t("The organizational structure changed in the Diagnostic. Save or discard your changes before syncing.")
        : t("The organizational structure changed in the Diagnostic.")}
      action={{
        label: <><RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />{t("Click here to sync")}</>,
        onClick: () => void run(),
        disabled: pending || hasDraft,
      }}
    />
  );
}
